// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import './PodcastHome.css';
import { API_ENDPOINTS } from '../config/api';
import { playPodcastGlobal } from './GlobalAudioPlayer';
import { getPodcastProgressKey, readPodcastProgressMap, formatSecondsCompact, isSamePodcast, hydratePodcastProgressFromServer } from './playbackProgress';
import BlogHeaderAd from '../Blog/BlogHeaderAd';
import TapeLoader from '../components/TapeLoader';
import PaginationControls from '../components/ui/PaginationControls';
import CrossPlatformRSS from '../components/CrossPlatformRSS/CrossPlatformRSS';
import { robustFetch } from '../utils/apiUtils';

const STORAGE_KEY = 'globalPodcastPlayer';


function PodcastHighlightText({ text, query }) {
    if (!query || !text) return <>{text}</>;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    const lowerQuery = query.toLowerCase();
    return (
        <>
            {parts.map((part, i) =>
                part.toLowerCase() === lowerQuery ? (
                    <mark key={i} className="podcast-search-highlight">{part}</mark>
                ) : (
                    part
                )
            )}
        </>
    );
}

function PodcastData() {
    const navigate = useNavigate();
    const [hasHeaderAd, setHasHeaderAd] = useState(true);
    const [podcasts, setPodcasts] = useState([]);
    const [currentPodcast, setCurrentPodcast] = useState(null); // Keep for UI feedback
    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = sessionStorage.getItem('podcast_last_page');
        return saved ? parseInt(saved, 10) : 1;
    });
    const isFirstMount = useRef(true);

    useEffect(() => {
        sessionStorage.setItem('podcast_last_page', currentPage.toString());
    }, [currentPage]);


    const [isMobileOrTabletPortrait, setIsMobileOrTabletPortrait] = useState(false);
    const [episodesPerPage, setEpisodesPerPage] = useState(10);

    // ========== SEARCH STATE ==========
    const [searchQuery, setSearchQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);

    // ========== LAYOUT TOGGLE STATE ==========
    const [viewLayout, setViewLayout] = useState(() => {
        if (typeof window !== 'undefined' && window.innerWidth <= 480) return 'list';
        return 'grid';
    });

    // ========== SORT STATE ==========
    const [sortBy, setSortBy] = useState('number'); // 'number' | 'date'
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'

    // eslint-disable-next-line no-unused-vars
    function getCurrentUserId() {
        const token = localStorage.getItem("token");
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.userId;
        } catch (e) {
            return null;
        }
    }


    useEffect(() => {
        setLoading(true); // Start loading
        setLoadingProgress(0); // Reset progress

        console.log('🎙️ Fetching podcasts from:', API_ENDPOINTS.PODCAST.ALL);
        
        // Use a flag to prevent state updates if unmounted
        let isMounted = true;

        const fetchData = async () => {
            try {
                const response = await robustFetch(API_ENDPOINTS.PODCAST.ALL);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Get content length for progress
                const contentLength = response.headers.get('content-length');
                // Even if no specific content length, we can show some activity
                const total = contentLength ? parseInt(contentLength, 10) : 0;
                let loaded = 0;

                // If we have a total, we can track real progress
                // If not, we fallback to a simulated progress driven by a timer (handled separately or parallel)
                // Since this component manages 'loadingProgress' directly without an internal fallback in a child component like BookLoader,
                // we'll implement a simple simulation loop here if total is 0.
                
                let simulationInterval;
                if (total === 0) {
                     // Start simulation because we can't track real percent
                     simulationInterval = setInterval(() => {
                        if (isMounted) {
                            setLoadingProgress(prev => {
                                if (prev >= 90) return prev;
                                const increment = prev < 50 ? 5 : prev < 80 ? 2 : 0.5;
                                return Math.min(prev + increment, 90);
                            });
                        }
                     }, 100);
                } else {
                     setLoadingProgress(0);
                }

                const reader = response.body.getReader();
                const chunks = [];

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) break;

                    if (value) {
                        chunks.push(value);
                        loaded += value.length;

                        if (isMounted && total > 0) {
                            // Calculate real percentage only if valid total
                            setLoadingProgress((loaded / total) * 100);
                        }
                    }
                }

                if (simulationInterval) clearInterval(simulationInterval);

                // Combine chunks back into a blob/text
                const blob = new Blob(chunks);
                const rawText = await blob.text();
                // Safe parse guard against Render.com cold-start returning "undefined"
                const trimmed = rawText ? rawText.trim() : '';
                if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                    if (isMounted) setPodcasts([]);
                    return;
                }
                let data;
                try { data = JSON.parse(trimmed); } catch(e) {
                    if (isMounted) setPodcasts([]);
                    return;
                }

                if (isMounted) {
                    console.log('📦 Podcast data received:', data);
                    
                    if (Array.isArray(data)) {
                        const sortedPodcasts = data.sort((a, b) => {
                            const dateA = new Date(a.date || a.createdAt || 0);
                            const dateB = new Date(b.date || b.createdAt || 0);
                            return dateB - dateA;
                        });
                        
                        setPodcasts(sortedPodcasts);
                    } else {
                        console.error('❌ Error: Unexpected podcast response format', data);
                        setPodcasts([]);
                    }
                }
            } catch (err) {
                if (isMounted) {
                    console.error('❌ Error fetching podcasts:', err);
                    setPodcasts([]);
                }
            } finally {
                if (isMounted) {
                    setLoadingProgress(100);
                    // Small delay to ensure 100% is seen
                    setTimeout(() => {
                        if (isMounted) setLoading(false);
                    }, 300);
                    console.log('🏁 Podcast loading finished');
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, []);


    // Detect mobile/tablet portrait for pagination
    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            setIsMobileOrTabletPortrait(width <= 1024);
        };
        
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Reset to page 1 when screen size or episodes per page changes
    useEffect(() => {
        if (isFirstMount.current) return;
        setCurrentPage(1);
    }, [isMobileOrTabletPortrait, episodesPerPage]);

    // Reset to page 1 when search changes
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        setCurrentPage(1);
    }, [searchQuery]);
    
    useEffect(() => {
        if (currentPodcast) {
            document.title = `${currentPodcast.title} | HariVishnu Insights`;
        } else {
            document.title = "Podcast | Hari Vishnu Insights";
        }
    }, [currentPodcast]);
    
    // State to track global audio player's playing status
    const [isGlobalPlaying, setIsGlobalPlaying] = useState(false);
    const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
    const [currentPlaybackDuration, setCurrentPlaybackDuration] = useState(0);
    const [playbackProgressMap, setPlaybackProgressMap] = useState({});
    
    // Listen for global audio player state changes to update local UI
    useEffect(() => {
        const checkGlobalPlayer = () => {
            const savedState = sessionStorage.getItem(STORAGE_KEY);
            setPlaybackProgressMap(readPodcastProgressMap());
            if (savedState) {
                try {
                    const state = JSON.parse(savedState);
                    if (state.currentPodcast) {
                        setCurrentPodcast(state.currentPodcast);
                        setIsGlobalPlaying(state.isPlaying || false);
                        setCurrentPlaybackTime(state.currentTime || 0);
                        setCurrentPlaybackDuration(state.duration || 0);
                    } else {
                        setCurrentPodcast(null);
                        setIsGlobalPlaying(false);
                        setCurrentPlaybackTime(0);
                        setCurrentPlaybackDuration(0);
                    }
                } catch (e) {
                    // Ignore errors
                }
            } else {
                setCurrentPodcast(null);
                setIsGlobalPlaying(false);
                setCurrentPlaybackTime(0);
                setCurrentPlaybackDuration(0);
            }
        };

        const handleAudioPlayingChange = (event) => {
            const state = event && event.detail;
            if (!state) return;

            if (state.currentPodcast && state.audioPlayerVisible) {
                setCurrentPodcast(state.currentPodcast);
                setIsGlobalPlaying(!!state.isPlaying);
                setCurrentPlaybackTime(state.currentTime || 0);
                setCurrentPlaybackDuration(state.duration || 0);
            } else {
                setCurrentPodcast(null);
                setIsGlobalPlaying(false);
                setCurrentPlaybackTime(0);
                setCurrentPlaybackDuration(0);
            }
            setPlaybackProgressMap(readPodcastProgressMap());
        };
        
        // Check on mount
        checkGlobalPlayer();
        void hydratePodcastProgressFromServer().then((map) => setPlaybackProgressMap(map || {}));
        
        // Listen for storage changes (from other tabs/components)
        window.addEventListener('storage', checkGlobalPlayer);
        window.addEventListener('audioPlayingChange', handleAudioPlayingChange);
        
        return () => {
            window.removeEventListener('storage', checkGlobalPlayer);
            window.removeEventListener('audioPlayingChange', handleAudioPlayingChange);
        };
    }, []);

    // Pagination logic based on device
    useEffect(() => {
        const updateEpisodesPerPage = () => {
            const width = window.innerWidth;
            if (width <= 480) {
                setEpisodesPerPage(10); // Mobile: 2x5
            } else if (width <= 1024) {
                setEpisodesPerPage(10); // Tablet Portrait: 2x5
            } else {
                setEpisodesPerPage(16); // Laptop: 4x4
            }
        };
        
        updateEpisodesPerPage();
        window.addEventListener('resize', updateEpisodesPerPage);
        return () => window.removeEventListener('resize', updateEpisodesPerPage);
    }, []);

    useEffect(() => {
        if (!isFirstMount.current) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [currentPage]);

    const playPodcast = (podcast) => {
        console.log('[PodcastHome] playPodcast called with:', {
            id: podcast._id,
            title: podcast.title,
            audioFile: podcast.audioFile
        });
        // Check if this is the currently playing podcast
        const savedState = sessionStorage.getItem(STORAGE_KEY);
        let shouldPlay = true;
        
        if (savedState) {
            try {
                const state = JSON.parse(savedState);
                if (state.currentPodcast && isSamePodcast(state.currentPodcast, podcast)) {
                    // Same podcast - toggle play/pause
                    shouldPlay = !state.isPlaying;
                }
            } catch (e) {
                // Ignore errors
            }
        }
        
        if (shouldPlay) {
            // Use global audio player
            playPodcastGlobal(podcast);
        } else {
            // Toggle pause by dispatching a pause event
            window.dispatchEvent(new CustomEvent('pausePodcast'));
        }
        
        // Also update local state for UI feedback (like highlighting current podcast)
        setCurrentPodcast(podcast);
    };

    if (loading) return <TapeLoader progress={loadingProgress} />;

    const getEpisodeUiState = (podcast) => {
        const key = getPodcastProgressKey(podcast);
        const progress = key ? playbackProgressMap[key] : null;
        const isCurrentEpisode = !!(currentPodcast && isSamePodcast(currentPodcast, podcast));
        const isPlayingNow = isCurrentEpisode && isGlobalPlaying;

        const playedTime = isCurrentEpisode ? (currentPlaybackTime || progress?.currentTime || 0) : (progress?.currentTime || 0);
        const totalDuration = isCurrentEpisode ? (currentPlaybackDuration || progress?.duration || 0) : (progress?.duration || 0);

        const started = !!(progress?.started || playedTime > 1);
        const completed = !!progress?.completed;
        const leftSeconds = totalDuration > 0 ? Math.max(0, totalDuration - playedTime) : 0;
        const progressPct = totalDuration > 0 ? Math.min(100, Math.max(0, (playedTime / totalDuration) * 100)) : 0;

        if (completed) {
            return { mode: 'completed', isPlayingNow, leftText: '0s', progressPct: 100 };
        }
        if (started || isPlayingNow) {
            return { mode: 'progress', isPlayingNow, leftText: formatSecondsCompact(leftSeconds), progressPct };
        }
        return { mode: 'idle', isPlayingNow, leftText: '', progressPct: 0 };
    };

    const renderEpisodeButtonContent = (uiState, idleText) => {
        if (uiState.mode === 'completed') {
            return <i className="fi fi-br-rotate-right"></i>;
        }

        if (uiState.mode === 'progress') {
            return (
                <div className="podcast-play-status">
                    <div className={`podcast-mini-eq${uiState.isPlayingNow ? ' is-playing' : ''}`}>
                        <span></span><span></span><span></span><span></span>
                    </div>
                    <div className="podcast-play-progress-line">
                        <span style={{ width: `${uiState.progressPct}%` }}></span>
                    </div>
                    <span className="podcast-left-time">{uiState.leftText} left</span>
                </div>
            );
        }

        return (
            <>
                <i className="fi fi-sr-play"></i>
                <span>{idleText}</span>
            </>
        );
    };

    // Format date helper (component-level so usable in both grid and list renders)
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = moment(dateString);
        const now = moment();
        const diffMinutes = now.diff(date, 'minutes');
        if (diffMinutes < 60) {
            if (diffMinutes < 1) return 'just now';
            return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
        }
        const diffHours = now.diff(date, 'hours');
        if (diffHours < 24) {
            return date.fromNow();
        } else {
            return date.format('DD MMM YYYY');
        }
    };

    // Apply search filter then sort
    const filteredPodcasts = searchQuery.trim()
        ? podcasts.filter(p => (p.title || '').toLowerCase().includes(searchQuery.trim().toLowerCase()))
        : [...podcasts];

    filteredPodcasts.sort((a, b) => {
        if (sortBy === 'date') {
            const dateA = new Date(a.date || a.createdAt || 0).getTime();
            const dateB = new Date(b.date || b.createdAt || 0).getTime();
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        }
        const numA = a.podcastNumber || 0;
        const numB = b.podcastNumber || 0;
        return sortOrder === 'desc' ? numB - numA : numA - numB;
    });

    const totalPages = Math.ceil(filteredPodcasts.length / episodesPerPage);
    const startIndex = (currentPage - 1) * episodesPerPage;
    const endIndex = startIndex + episodesPerPage;
    const currentPodcasts = filteredPodcasts.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Function to correct thumbnail URL based on audio file pattern
    const correctThumbnailUrl = (podcast) => {
        if (!podcast || !podcast.audioFile) {
            return (podcast && podcast.thumbnail) || null;
        }

        try {
            // Extract podcast number from audio file URL
            const audioMatch = podcast.audioFile.match(/\/(?:Podcast-|episode-)(\d+)\//);
            const podcastNumber = audioMatch ? audioMatch[1] : null;

            if (podcastNumber) {
                // Construct correct thumbnail URL
                const baseUrl = 'https://podcast-audio-episodes.s3.eu-north-1.amazonaws.com';
                const correctPath = `episode-${podcastNumber}/Podcast+${podcastNumber}+Thumbnail.jpg`;
                const correctUrl = `${baseUrl}/${correctPath}`;
                return correctUrl;
            }
        } catch (error) {
            console.error('[PodcastHome] Error correcting thumbnail URL:', error);
        }

        // Fallback to original thumbnail if correction fails
        return (podcast && podcast.thumbnail) || null;
    };
    
    // Helper to extract a URL from an image field (string or [{url, ...}] array)
    const extractImageUrl = (imgField) => {
        if (!imgField) return null;
        if (typeof imgField === 'string') return imgField;
        if (Array.isArray(imgField) && imgField.length > 0) {
            for (let i = imgField.length - 1; i >= 0; i--) {
                const img = imgField[i];
                if (img && img.url && img.imageTitle) return img.url;
            }
            for (let i = imgField.length - 1; i >= 0; i--) {
                const img = imgField[i];
                if (img && img.url) return img.url;
            }
        }
        return null;
    };

    // Helper function to get host image
    const getHostImage = (host) => {
        if (!host) return null;

        if (typeof host === 'object' && !Array.isArray(host)) {
            const keys = Object.keys(host).filter(k => !isNaN(parseInt(k)));
            const isObjectIdObject = keys.length > 0 && keys.length <= 24 &&
                keys.every(k => {
                    const num = parseInt(k);
                    return num >= 0 && num < 24 && typeof host[k] === 'string' && host[k].length === 1;
                });

            if (isObjectIdObject) return null;

            return extractImageUrl(host.hostImage) || extractImageUrl(host.image) || host.avatar || host.profileImage || null;
        } else if (typeof host === 'string') {
            if (host.startsWith('http://') || host.startsWith('https://') || host.startsWith('data:')) {
                return host;
            }
        }
        return null;
    };



    return (
        <div className="podcast mx-auto w-full max-w-[2000px] px-3 pb-8 pt-6 md:px-4 lg:px-4">
            {/* Blog-style header */}
            <div className="podcast-header-section mb-2 rounded-2xl p-4 md:p-5">
                <div className={`podcast-header-top-row${hasHeaderAd ? '' : ' no-active-ad'}`}>
                    <h1 className={`podcast-header-text${hasHeaderAd ? '' : ' centered'}`}>Podcast</h1>
                    <div className={`podcast-header-ad-wrapper${hasHeaderAd ? '' : ' is-hidden'}`}>
                        <BlogHeaderAd onAvailabilityChange={setHasHeaderAd} />
                    </div>
                </div>
                <CrossPlatformRSS
                    items={[
                        { 
                            link: "https://podcasts.apple.com/us/podcast/i-tested-3-ai-browsers-heres-what-actually-works/id1856935035?i=1000748469002&itscg=30200&itsct=podcast_box_badge&ls=1&mttnsubad=1000748469002", 
                            label: "Apple Podcasts", 
                            isImage: true,
                            imageSrc: "https://toolbox.marketingtools.apple.com/api/v2/badges/listen-on-apple-podcasts/badge-26/en-us",
                            mobileImageSrc: "https://toolbox.marketingtools.apple.com/api/v2/badges/app-icon-podcasts/standard-25/en-us",
                            color: "transparent",
                            hideLabel: true
                        },
                        { 
                            link: "https://creators.spotify.com/pod/show/0sybna9wE2KU4uLeJeG1p4/home", 
                            label: "Listen on", 
                            subLabel: "Spotify",
                            icon: "fi fi-brands-spotify", 
                            color: "#1DB954" 
                        },
                        { 
                            link: "/podcast-rss.xml", 
                            label: "Podcast RSS", 
                            icon: "fi fi-sr-rss", 
                            color: "#ff9800" 
                        },
                        {
                            label: "Copy Link",
                            icon: "fi fi-rr-copy-alt",
                            isCopy: true,
                            copyValue: "/podcast-rss.xml",
                            color: "#5c6bc0"
                        }
                    ]}
                />
                <div className="podcast-header-filters">
                    <div className={`podcast-search-wrapper${searchFocused ? " is-focused" : ""}`}>
                        <div className="podcast-search-inner">
                            <i className="fi fi-rr-search"></i>
                            <span className="podcast-search-sep"></span>
                            <input
                                type="text"
                                className="podcast-search-input"
                                placeholder="Search episodes..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                            />
                            {searchQuery && (
                                <button
                                    className="podcast-search-clear"
                                    onClick={() => setSearchQuery('')}
                                    aria-label="Clear search"
                                    type="button"
                                >
                                    <i className="fi fi-rr-cross-small"></i>
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="podcast-filters-row">
                        <div className="sort-feature-container">
                            <span className="sort-label">Sort:</span>
                            <select
                                className="sort-select"
                                value={sortBy}
                                onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                            </select>
                            <button
                                className="sort-order-btn"
                                type="button"
                                onClick={() => { setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); setCurrentPage(1); }}
                                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                            >
                                <i className={`fi fi-rr-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                <span className="sort-order-letter">{sortOrder === 'asc' ? 'A' : 'D'}</span>
                            </button>
                        </div>
                        <div className="podcast-align-toggle-container">
                            <span className="podcast-filter-label">Layout</span>
                            <button
                                className="podcast-align-toggle-btn"
                                type="button"
                                onClick={() => setViewLayout(v => v === 'grid' ? 'list' : 'grid')}
                                title={viewLayout === 'grid' ? 'Switch to List view' : 'Switch to Grid view'}
                            >
                                {viewLayout === 'grid' ? (
                                    <i className="fi fi-sr-rectangle-list"></i>
                                ) : (
                                    <i className="fi fi-sr-grid"></i>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Main content area */}
            <div className="podcast-main-layout grid gap-5">
                <div className="podcast-list-container min-w-0">
                    {/* Podcasts list */}
                    <div className={`podcast-list podcast-list-grid${viewLayout === 'list' ? ' podcast-list-layout' : ''} rounded-2xl p-3 md:p-4`}>
                        {filteredPodcasts.length === 0 ? (
                            <div className="podcast-empty-state">
                                {searchQuery.trim() ? (
                                    <p>No episodes found for "{searchQuery.trim()}".</p>
                                ) : (
                                    <p>No podcasts available at this time.</p>
                                )}
                            </div>
                        ) : (
                            currentPodcasts.map(podcast => {
                                const thumbnailUrl = correctThumbnailUrl(podcast);
                                const isPlaying = !!(currentPodcast && isSamePodcast(currentPodcast, podcast) && isGlobalPlaying);
                                const uiState = getEpisodeUiState(podcast);

                                // ---- LIST VIEW ----
                                if (viewLayout === 'list') {
                                    return (
                                        <div
                                            key={podcast._id}
                                            className="podcast-lv-item"
                                            onClick={() => podcast.slug && navigate(`/podcast/${podcast.slug}`)}
                                            style={{ cursor: podcast.slug ? 'pointer' : 'default' }}
                                        >
                                            <div className="podcast-lv-thumb">
                                                {thumbnailUrl ? (
                                                    <img
                                                        src={thumbnailUrl}
                                                        alt={podcast.title}
                                                        loading="lazy"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : null}
                                            </div>
                                            <div className="podcast-lv-details">
                                                <div className="podcast-lv-meta-top">
                                                    {podcast.podcastNumber ? (
                                                        <span className="podcast-lv-ep-number">#{podcast.podcastNumber}</span>
                                                    ) : null}
                                                    <div className="podcast-lv-play-wrapper" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            className="podcast-episode-play-button"
                                                            onClick={(e) => { e.stopPropagation(); playPodcast(podcast); }}
                                                            title={uiState.mode === 'completed' ? 'Replay' : (isPlaying ? 'Pause' : 'Play')}
                                                        >
                                                            {renderEpisodeButtonContent(uiState, 'Play')}
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className="podcast-lv-title">
                                                    <PodcastHighlightText text={podcast.title} query={searchQuery} />
                                                </p>
                                                <div className="podcast-lv-footer">
                                                    <div className="podcast-lv-hosts">
                                                        {podcast.hosts && Array.isArray(podcast.hosts) && podcast.hosts.slice(0, 2).map((host, index) => {
                                                            const hostImage = getHostImage(host);
                                                            if (!hostImage) return null;
                                                            return (
                                                                <img
                                                                    key={index}
                                                                    className="podcast-lv-host-img"
                                                                    src={hostImage}
                                                                    alt={`Host ${index + 1}`}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                    <span className="podcast-lv-date">{formatDate(podcast.date)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // ---- GRID VIEW (original) ----
                                return (
                                    <div key={podcast._id} className="podcast-episode-card">
                                        <div
                                            className="podcast-episode-card-content"
                                            onClick={() => {
                                                if (podcast.slug) {
                                                    navigate(`/podcast/${podcast.slug}`);
                                                }
                                            }}
                                            style={{ cursor: podcast.slug ? 'pointer' : 'default' }}
                                        >
                                            {/* Content overlay */}
                                            <div className="podcast-episode-card-overlay">
                                                <div className="podcast-episode-card-top">
                                                    {/* Thumbnail */}
                                                    <div className="podcast-episode-card-thumbnail">
                                                        {thumbnailUrl ? (
                                                            <img
                                                                src={thumbnailUrl}
                                                                alt={podcast.title}
                                                                className="podcast-episode-card-thumbnail-img"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        ) : null}
                                                        {podcast.podcastNumber ? (
                                                            <span className="podcast-ep-number-badge">{podcast.podcastNumber}</span>
                                                        ) : null}
                                                    </div>

                                                    {/* Title */}
                                                    <div className="podcast-episode-card-title">
                                                        <h3><PodcastHighlightText text={podcast.title} query={searchQuery} /></h3>
                                                    </div>

                                                    {/* Date */}
                                                    <div className="podcast-episode-date">{formatDate(podcast.date)}</div>
                                                </div>

                                                {/* Bottom section: Hosts and Play Button */}
                                                <div className="podcast-episode-card-bottom">
                                                    <div className="podcast-episode-host-list">
                                                        {podcast.hosts && Array.isArray(podcast.hosts) && podcast.hosts.length > 0 ? (
                                                            <>
                                                                {podcast.hosts.slice(0, 3).map((host, index) => {
                                                                    const hostImage = getHostImage(host);
                                                                    if (!hostImage) return null;

                                                                    return (
                                                                        <img
                                                                            key={index}
                                                                            className="podcast-episode-host-img"
                                                                            src={hostImage}
                                                                            alt={`Host ${index + 1}`}
                                                                            style={{ zIndex: podcast.hosts.length - index }}
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                                {podcast.hosts.length > 3 && (
                                                                    <div className="podcast-episode-more-hosts">
                                                                        +{podcast.hosts.length - 3}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : null}
                                                    </div>
                                                    <div className="podcast-episode-play-wrapper" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                className="podcast-episode-play-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    playPodcast(podcast);
                                                                }}
                                                                title={uiState.mode === 'completed' ? 'Replay' : (isPlaying ? 'Pause' : 'Play')}
                                                            >
                                                                {renderEpisodeButtonContent(uiState, podcast.podcastNumber ? `Play #${podcast.podcastNumber}` : 'Play')}
                                                            </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <PaginationControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            containerClassName="podcast-pagination"
                            buttonClassName="podcast-pagination-btn"
                            infoClassName="podcast-page-info"
                            previousDisabled={currentPage === 1}
                            nextDisabled={currentPage === totalPages}
                            onFirst={() => handlePageChange(1)}
                            onPrevious={() => handlePageChange(currentPage - 1)}
                            onNext={() => handlePageChange(currentPage + 1)}
                            onLast={() => handlePageChange(totalPages)}
                        />
                    )}
                </div>
            </div>

            {/* Audio player is now handled by GlobalAudioPlayer component */}
        </div>
    );
}

export default function PodcastHome() {
    return (
        <div className='podcast-home min-h-screen bg-bg'>
            <PodcastData />
        </div>
    );
}